import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";
import { PenTool, Printer, Flame, Scissors, Ruler, Package, Truck, ShieldCheck, ArrowUpRight, ArrowDown } from "lucide-react";
import { IMG, PROCESS } from "./siteData";
import { useAdminImages, ChangeBtn } from "./AboutPremium";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { PenTool, Printer, Flame, Scissors, Ruler, Package, Truck, ShieldCheck };
const EASE = [0.22, 1, 0.36, 1];
const FOIL = "linear-gradient(100deg,#8B6B3A 0%,#E9D3A1 25%,#B89B72 45%,#F4E4BC 60%,#8B6B3A 100%)";
const WEAVE = "repeating-linear-gradient(0deg,rgba(255,255,255,0.02) 0 1px,transparent 1px 3px),repeating-linear-gradient(90deg,rgba(255,255,255,0.02) 0 1px,transparent 1px 3px)";
const LINEN = "repeating-linear-gradient(0deg,rgba(139,107,58,0.035) 0 1px,transparent 1px 4px)";
const WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];
const zigBottom = (n, d) => {
  const p = ["0 0", "100% 0", `100% calc(100% - ${d}px)`];
  for (let i = n - 1; i >= 0; i--) p.push(`${((i + 0.5) / n) * 100}% 100%`, `${(i / n) * 100}% calc(100% - ${d}px)`);
  return `polygon(${p.join(",")})`;
};
const zigSides = (n = 5, d = 6) => {
  const p = ["0 0", "100% 0"];
  for (let i = 0; i < n; i++) p.push(`calc(100% - ${d}px) ${((i + 0.5) / n) * 100}%`, `100% ${((i + 1) / n) * 100}%`);
  p.push("0 100%");
  for (let i = n - 1; i >= 0; i--) p.push(`${d}px ${((i + 0.5) / n) * 100}%`, `0 ${(i / n) * 100}%`);
  return `polygon(${p.join(",")})`;
};
const TAG = zigSides(5, 6);
const up = (d = 0) => ({ initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { delay: d, duration: 0.8, ease: EASE } });
const STAGE_FALLBACK = [IMG.printedFolds, IMG.printMachine, IMG.printMachine2, IMG.fashionSheer, IMG.printedRack, IMG.kurtiRedStreet, IMG.studioBlack, IMG.eventGala, IMG.weddingArch, IMG.printedScarves];
const ROW = 270;          // desktop row height (px) — keeps the thread aligned
const VB_W = 1200;        // svg viewBox width

function useImages() {
  const A = useAdminImages() || {};
  const isAdmin = !!A.isAdmin;
  const reload = A.reload || (() => {});
  const src = (slot, fb) => (A.src ? A.src(slot, fb) : A.cfg && A.cfg[slot] ? `${API}/site/fabric-images/${A.cfg[slot]}` : fb);
  return { isAdmin, reload, src };
}

function Change({ slot, onDone, className = "" }) {
  return <div className={`absolute z-20 ${className}`}><ChangeBtn slot={slot} onDone={onDone} /></div>;
}

function threadPath(n) {
  let d = `M600,0 `, px = 600, py = 0;
  for (let i = 0; i < n; i++) {
    const cx = i % 2 === 0 ? 520 : 680, cy = ROW / 2 + ROW * i;
    d += `C${px},${py + 130} ${cx},${cy - 130} ${cx},${cy} `;
    px = cx; py = cy;
  }
  return d + `C${px},${py + 130} 600,${py + 120} 600,${py + 210}`;
}

function Hero({ src, isAdmin, reload, n }) {
  return (
    <section className="relative overflow-hidden bg-[#16120E] text-[#FAF9F6]" data-testid="mfg-hero">
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: WEAVE }} />
      <div aria-hidden className="pointer-events-none absolute -top-32 right-40 h-[620px] w-[620px] rounded-full bg-[#C9A96E]/[0.13] blur-[110px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-6 pb-24 pt-16 lg:grid-cols-[1fr_520px] lg:px-12 lg:pt-20">
        <div>
          <motion.p {...up(0)} className="flex items-center gap-4 text-[11px] uppercase tracking-[0.34em] text-[#C9A96E]"><span className="h-px w-10 bg-[#C9A96E]" />Manufacturing excellence</motion.p>
          <motion.h1 {...up(0.1)} className="font-display mt-6 text-5xl font-light leading-[1] sm:text-6xl lg:text-[80px]">
            {WORDS[n] || n} stages.<br />One obsession<br />with <em className="bg-clip-text pr-2 text-transparent" style={{ backgroundImage: FOIL }}>finish.</em>
          </motion.h1>
          <motion.p {...up(0.2)} className="mt-6 max-w-md text-[15px] leading-relaxed text-white/60">From artwork to doorstep, every metre moves across our Tiruppur studio floor — printed, fused, stitched and checked by hand.</motion.p>
          <motion.div {...up(0.3)} className="mt-9 flex flex-wrap gap-3.5">
            <Link to="/contact" style={{ clipPath: TAG, backgroundImage: FOIL }} className="relative inline-flex items-center gap-2 py-3.5 pl-11 pr-8 text-[13px] tracking-wide text-[#1A1410]">
              <span className="pointer-events-none absolute inset-[5px] border border-dashed border-[#1A1410]/30" /><span className="absolute left-4 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[#1A1410]" />Request a sample <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href="#process" style={{ clipPath: TAG }} className="relative inline-flex items-center gap-2 bg-white/[0.07] px-8 py-3.5 text-[13px] tracking-wide transition-colors hover:bg-white/[0.14]">
              <span className="pointer-events-none absolute inset-[5px] border border-dashed border-white/20" />See the process <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="relative mx-auto w-full max-w-[520px]">
          <div className="relative z-10 -mx-2.5 h-[30px] rounded-[15px] bg-gradient-to-b from-[#c49a62] via-[#8a6236] to-[#5c3e20] shadow-[0_10px_18px_rgba(0,0,0,0.45),inset_0_3px_0_rgba(255,255,255,0.25)]">
            <span className="absolute -left-2 -top-1.5 h-[42px] w-[22px] rounded-lg bg-gradient-to-r from-[#4a3018] via-[#8a6236] to-[#4a3018]" />
            <span className="absolute -right-2 -top-1.5 h-[42px] w-[22px] rounded-lg bg-gradient-to-r from-[#4a3018] via-[#8a6236] to-[#4a3018]" />
          </div>
          <motion.div style={{ transformOrigin: "50% 0%", filter: "drop-shadow(0 30px 40px rgba(0,0,0,0.6))" }} animate={{ rotate: [0.6, -0.6, 0.6] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} className="-mt-2">
            <div className="relative h-[360px] sm:h-[440px]" style={{ clipPath: zigBottom(20, 12) }}>
              <img src={src("mfg_hero", IMG.printMachine)} alt="Nayara studio printing floor" className="h-full w-full object-cover" />
              <div className="pointer-events-none absolute inset-0 mix-blend-overlay" style={{ background: "repeating-linear-gradient(90deg,rgba(0,0,0,0.18) 0,rgba(255,255,255,0.06) 60px,rgba(0,0,0,0.18) 120px)" }} />
              <span className="pointer-events-none absolute inset-x-3.5 bottom-6 top-3.5 border-[1.5px] border-dashed border-[#F4E4BC]/50" />
            </div>
          </motion.div>
          <div className="absolute -right-6 top-16 z-20 flex rotate-[8deg] flex-col items-center" style={{ filter: "drop-shadow(0 8px 10px rgba(0,0,0,0.4))" }} aria-hidden>
            <span className="h-7 w-px bg-[#C9A96E]" />
            <span className="bg-[#F4E4BC] px-4 pb-2.5 pt-4 text-center text-[#3a2a1a]" style={{ clipPath: zigSides(3, 5) }}>
              <small className="block text-[9px] tracking-[0.3em] text-[#8B6B3A]">EST. TIRUPPUR</small><span className="font-display text-[22px]">Studio floor</span>
            </span>
          </div>
          {isAdmin && <Change slot="mfg_hero" onDone={reload} className="left-6 top-14" />}
        </motion.div>
      </div>
      <div aria-hidden className="absolute bottom-0 right-0 top-0 hidden w-[34px] flex-col bg-[#E9C46A] pt-2.5 shadow-[-4px_0_10px_rgba(0,0,0,0.3)] xl:flex">
        {Array.from({ length: 70 }).map((_, j) => (
          <span key={j} className={`border-t border-[#5a3d22] pl-3 text-[8px] leading-none text-[#3a2a1a] ${j % 5 === 0 ? "w-4" : "w-2.5"}`} style={{ height: 12 }}>{j % 10 === 0 ? j : ""}</span>
        ))}
      </div>
    </section>
  );
}

function GoldenThread({ src, isAdmin, reload }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const n = PROCESS.length;
  const total = ROW * n + 230;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 70%"] });
  const draw = useSpring(scrollYProgress, { stiffness: 80, damping: 20 });
  const d = threadPath(n);
  return (
    <section id="process" className="relative px-6 pb-10 pt-24 lg:px-12" data-testid="mfg-process">
      <div className="text-center">
        <motion.p {...up(0)} className="flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.34em] text-[#8B6B3A]"><span className="h-px w-10 bg-[#8B6B3A]" />The journey of a metre<span className="h-px w-10 bg-[#8B6B3A]" /></motion.p>
        <motion.h2 {...up(0.1)} className="font-display mt-4 text-4xl font-light sm:text-5xl lg:text-6xl">Follow the <em className="text-[#8B6B3A]">golden thread.</em></motion.h2>
        <motion.p {...up(0.2)} className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b625a]">One thread runs through everything we make — from the first sketch to the parcel at your door.</motion.p>
      </div>

      <div ref={ref} className="relative mx-auto mt-20 max-w-[1200px] md:mt-24" style={{ minHeight: 0 }}>
        {/* desktop thread */}
        <svg aria-hidden className="pointer-events-none absolute left-0 top-0 hidden h-full w-full md:block" viewBox={`0 0 ${VB_W} ${total}`} preserveAspectRatio="none" style={{ height: total }}>
          <defs><linearGradient id="gthread" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E9D3A1" /><stop offset=".5" stopColor="#B89B72" /><stop offset="1" stopColor="#8B6B3A" /></linearGradient></defs>
          <path d={d} fill="none" stroke="rgba(139,107,58,0.18)" strokeWidth="1" strokeDasharray="2 9" vectorEffect="non-scaling-stroke" />
          <motion.path d={d} fill="none" stroke="url(#gthread)" strokeWidth="2.2" vectorEffect="non-scaling-stroke" style={{ pathLength: reduce ? 1 : draw }} />
        </svg>
        <svg aria-hidden className="absolute -top-16 left-1/2 hidden -translate-x-1/2 rotate-[8deg] md:block" width="22" height="90" viewBox="0 0 22 90">
          <path d="M11 0 L14 70 L11 90 L8 70 Z" fill="#cfcfcf" stroke="#8a8a8a" strokeWidth=".8" /><ellipse cx="11" cy="12" rx="2" ry="6" fill="#FBF8F2" stroke="#8a8a8a" strokeWidth=".6" />
        </svg>
        {/* mobile thread */}
        <span aria-hidden className="absolute bottom-8 left-[47px] top-2 w-[2px] bg-gradient-to-b from-[#E9D3A1] via-[#B89B72] to-[#8B6B3A] md:hidden" />

        {PROCESS.map((step, i) => {
          const Icon = ICONS[step.icon] || PenTool;
          const left = i % 2 === 0;
          const slot = `mfg${i + 1}`;
          return (
            <div key={step.title} className="relative flex items-center gap-6 pb-12 md:block md:h-[270px] md:pb-0" data-testid={`mfg-step-${i + 1}`}>
              <motion.div initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
                className={`relative z-10 h-24 w-24 flex-none md:absolute md:top-1/2 md:h-[156px] md:w-[156px] md:-translate-y-1/2 ${left ? "md:left-[calc(43.333%_-_78px)]" : "md:left-[calc(56.667%_-_78px)]"}`}>
                <div className="relative h-full w-full rounded-full bg-[#FBF8F2] p-[6px] shadow-[0_0_0_1.5px_#C9A96E,0_18px_30px_rgba(0,0,0,0.18)] md:p-[7px]">
                  <span aria-hidden className="pointer-events-none absolute -inset-[9px] rounded-full border border-dashed border-[#8B6B3A]/45" />
                  <img src={src(slot, STAGE_FALLBACK[i % STAGE_FALLBACK.length] || IMG.printMachine)} alt={step.title} loading="lazy" className="h-full w-full rounded-full object-cover" />
                  <span className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full bg-[#2A2220] text-[#F4E4BC] shadow-md md:h-11 md:w-11"><Icon className="h-4 w-4 md:h-5 md:w-5" strokeWidth={1.5} /></span>
                </div>
                {isAdmin && <Change slot={slot} onDone={reload} className="left-1/2 top-full mt-3 -translate-x-1/2" />}
              </motion.div>
              <motion.div {...up(0.1)}
                className={`relative min-w-0 flex-1 md:absolute md:top-1/2 md:w-[380px] md:-translate-y-1/2 ${left ? "md:right-[calc(56.667%_+_110px)] md:text-right" : "md:left-[calc(56.667%_+_110px)]"}`}>
                <span aria-hidden className={`font-display pointer-events-none absolute -top-14 hidden text-[150px] font-light leading-none text-transparent md:block ${left ? "-right-2" : "-left-2"}`} style={{ WebkitTextStroke: "1px rgba(184,155,114,0.35)" }}>{String(i + 1).padStart(2, "0")}</span>
                <p className="relative text-[10px] uppercase tracking-[0.34em] text-[#8B6B3A] md:mt-10">Stage {String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display relative mt-1.5 text-3xl font-light leading-[1.05] md:text-[40px]">{step.title}</h3>
                <p className="relative mt-2.5 text-[14.5px] leading-relaxed text-[#6b625a]">{step.text}</p>
              </motion.div>
            </div>
          );
        })}
        <motion.div {...up(0)} className="relative flex flex-col items-center pt-4 md:h-[230px] md:justify-end md:pb-6 md:pt-0">
          <span className="h-2.5 w-16 rounded bg-gradient-to-b from-[#a8804c] to-[#6b4a2a]" />
          <span className="h-11 w-12 shadow-[inset_6px_0_10px_rgba(0,0,0,0.25)]" style={{ background: "repeating-linear-gradient(0deg,#C9A96E 0 2px,#e0c48e 2px 4px)" }} />
          <span className="h-2.5 w-16 rounded bg-gradient-to-b from-[#a8804c] to-[#6b4a2a]" />
          <p className="font-display mt-3 text-2xl italic text-[#8B6B3A]">Delivered with care.</p>
        </motion.div>
      </div>
    </section>
  );
}

function Polaroid({ img, caption, slot, isAdmin, reload, className, rot, d }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40, rotate: rot * 2 }} whileInView={{ opacity: 1, y: 0, rotate: rot }} whileHover={{ rotate: 0, y: -6, zIndex: 30 }}
      viewport={{ once: true }} transition={{ type: "spring", stiffness: 110, damping: 15, delay: d }}
      className={`absolute bg-white p-3 pb-11 shadow-[0_18px_30px_rgba(0,0,0,0.18)] ${className}`}>
      <span aria-hidden className="absolute -top-3 left-1/2 h-[26px] w-[90px] -translate-x-1/2 -rotate-[4deg] bg-[#C9A96E]/55" />
      <img src={img} alt={caption} loading="lazy" className="block h-full w-full object-cover" />
      <p className="font-display absolute inset-x-0 bottom-3 text-center text-xl italic text-[#5a4a3a]">{caption}</p>
      {isAdmin && <Change slot={slot} onDone={reload} className="left-3 top-3" />}
    </motion.div>
  );
}

function Inside({ src, isAdmin, reload, n }) {
  const stats = [[String(n), "Stage process", "#b8402f", "#d35a48"], ["1", "Roof, start to finish", "#1f3a5f", "#2f4f7a"],
    ["100%", "Orders inspected", "#C9A96E", "#e0c48e"], ["TN", "Made in Tiruppur", "#1f5136", "#2d6b4a"]];
  return (
    <section className="mx-auto grid max-w-[1300px] items-center gap-16 px-6 pb-28 pt-20 lg:grid-cols-2 lg:px-12" data-testid="mfg-inside">
      <div className="relative mx-auto h-[400px] w-full max-w-[580px] sm:h-[440px]">
        <Polaroid img={src("mfg_pol1", IMG.printMachine2 || IMG.printMachine)} caption="the press" slot="mfg_pol1" isAdmin={isAdmin} reload={reload} rot={-5} d={0} className="left-0 top-6 h-[260px] w-[64%] sm:h-[300px]" />
        <Polaroid img={src("mfg_pol2", IMG.printedRack || IMG.printedFolds)} caption="fresh off the line" slot="mfg_pol2" isAdmin={isAdmin} reload={reload} rot={4} d={0.1} className="right-0 top-24 h-[230px] w-[52%] sm:h-[260px]" />
        <Polaroid img={src("mfg_pol3", IMG.eventGala || IMG.studioBlack)} caption="finished" slot="mfg_pol3" isAdmin={isAdmin} reload={reload} rot={-1} d={0.2} className="bottom-0 left-[18%] h-[170px] w-[40%] sm:h-[190px]" />
      </div>
      <div>
        <motion.p {...up(0)} className="flex items-center gap-4 text-[11px] uppercase tracking-[0.34em] text-[#8B6B3A]"><span className="h-px w-10 bg-[#8B6B3A]" />Inside the studio</motion.p>
        <motion.h2 {...up(0.1)} className="font-display mt-4 text-4xl font-light leading-[1.05] sm:text-5xl">Machines do the printing.<br /><em className="text-[#8B6B3A]">People do the finishing.</em></motion.h2>
        <motion.p {...up(0.2)} className="mt-4 max-w-lg text-[15px] leading-relaxed text-[#6b625a]">Our in-house team in Tiruppur handles every stage under one roof — so colour, quality and deadlines stay in our hands, not a third party's.</motion.p>
        <div className="mt-9 grid grid-cols-2 gap-x-8 gap-y-7">
          {stats.map(([big, lb, c, c2], i) => (
            <motion.div key={lb} {...up(0.25 + i * 0.08)} className="flex items-center gap-4">
              <span className="flex flex-none flex-col items-center" aria-hidden>
                <span className="h-[9px] w-[54px] rounded bg-gradient-to-b from-[#a8804c] to-[#6b4a2a]" />
                <span className="h-11 w-10 shadow-[inset_6px_0_10px_rgba(0,0,0,0.25)]" style={{ background: `repeating-linear-gradient(0deg,${c} 0 2px,${c2} 2px 4px)` }} />
                <span className="h-[9px] w-[54px] rounded bg-gradient-to-b from-[#a8804c] to-[#6b4a2a]" />
              </span>
              <span><span className="font-display block text-[44px] font-light leading-none">{big}</span><span className="mt-1 block text-[10px] uppercase tracking-[0.24em] text-[#8B6B3A]">{lb}</span></span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Manufacturing() {
  const { isAdmin, reload, src } = useImages();
  const n = PROCESS.length;
  return (
    <div className="bg-[#FBF8F2] text-[#1A1A1A]" style={{ backgroundImage: LINEN }} data-testid="manufacturing-page">
      <Hero src={src} isAdmin={isAdmin} reload={reload} n={n} />
      <GoldenThread src={src} isAdmin={isAdmin} reload={reload} />
      <Inside src={src} isAdmin={isAdmin} reload={reload} n={n} />
    </div>
  );
}
